import { find, minBy } from 'lodash';

import { FastProductDetails, QuoteType } from '@common/types';
import insureAPIs from '@services/apis/insure';
import {
  ComboType,
  HealthMember,
  HealthPremium,
  HealthProductBoundary,
  HealthQuoteMember,
  HealthQuoteMemberGender,
  HealthQuotePlanDetails,
  Suggestion
} from '@store/healthquote';
import { useHealthQuoteStore } from '@store/healthquote/quote';
import { convertToINR } from '@utils';
import {
  HEALTH_INSURANCE_PRODUCTS,
  MEMBER_SELECTION_SUPPORTED_ADDONS
} from '@utils/constants';

import { HEALTH_PRODUCTS_SLUGS } from './constants';

export const useGenHealthPath = (path: string) => {
  const quote = useHealthQuoteStore.getState().quote;
  const product = useHealthQuoteStore.getState().plan.product;
  const slug = HEALTH_PRODUCTS_SLUGS[product];
  if (quote === QuoteType.Normal) {
    return `/health/quote/${path}`;
  } else {
    return `/health/fq/${slug}/${path}`;
  }
};

export const generateParentsLabel = (members: HealthMember[]): string => {
  const fatherIncluded = members.find(
    (item) => item.relation === HealthQuoteMember.Father
  );
  const motherIncluded = members.find(
    (item) => item.relation === HealthQuoteMember.Mother
  );

  if (fatherIncluded && motherIncluded) {
    return 'parents';
  } else if (fatherIncluded && !motherIncluded) {
    return 'father';
  } else if (!fatherIncluded && motherIncluded) {
    return 'mother';
  } else {
    return '';
  }
};

export const generateFamilyLabel = (members: HealthMember[]): string => {
  const selfIncluded = members.find(
    (item) => item.relation === HealthQuoteMember.Self
  );
  const spouseIncluded = members.find(
    (item) => item.relation === HealthQuoteMember.Spouse
  );
  const kidsIncluded = members.some(
    (item) =>
      item.relation === HealthQuoteMember.Son ||
      item.relation === HealthQuoteMember.Daughter
  );
  if ((selfIncluded || spouseIncluded) && kidsIncluded) {
    return 'your family';
  } else if (selfIncluded && !spouseIncluded) {
    return 'you';
  } else if (!selfIncluded && spouseIncluded) {
    return 'your spouse';
  } else if (selfIncluded && spouseIncluded) {
    return 'you & your spouse';
  } else {
    return 'your family';
  }
};

export const getMembersLabel = (combo: ComboType, members: HealthMember[]) => {
  if (combo === ComboType.Family || combo === ComboType.You) {
    return generateFamilyLabel(members);
  } else if (combo === ComboType.Parents) {
    return generateParentsLabel(members);
  }
};

export const getMembersLabelThirdPerson = (label: string) => {
  switch (label) {
    case 'your family':
      return 'your family members';
    case 'you':
      return 'your';
    case 'your spouse':
      return "your spouse's";
    case 'you & your spouse':
      return "you & your spouse's";
    case 'parents':
      return 'your parents';
    case 'father':
      return "your father's";
    case 'mother':
      return "your mother's";
  }
};

export const validatePincode = async (
  pincode: number,
  product?: string
): Promise<boolean> => {
  if (pincode.toString().length === 6) {
    if (product) {
      const resp = await insureAPIs.verifyHealthPincode({ pincode, product });
      return resp.data.data === 'Pincode is valid' ? true : false;
    } else {
      const resp = await insureAPIs.validateHealthPincode({ pincode });
      return resp.data.city ? true : false;
    }
  } else {
    return false;
  }
};

/**
 * Map form values to members array
 * @param members
 * @param values
 * @returns HealthMembers[]
 */
export const getMembersWithAgeAndMedConditions = (
  members: HealthMember[],
  values: any,
  conditionRequired = false
) => {
  let sonIndex = 0;
  let daughterIndex = 0;
  const latestMembers: HealthMember[] = [];
  members.forEach((member: HealthMember) => {
    const tempMember: HealthMember = { ...member };
    if (
      member.relation === HealthQuoteMember.Daughter ||
      member.relation === HealthQuoteMember.Son
    ) {
      let childIndex = sonIndex;
      if (member.relation === HealthQuoteMember.Daughter) {
        childIndex = daughterIndex;
        daughterIndex++;
      } else {
        sonIndex++;
      }
      tempMember.age = values[`${member.relation}${childIndex}age`];
      if (conditionRequired) {
        tempMember.med_conditions = values[`${member.relation}${childIndex}Diseases`]
          ?.length
          ? values[`${member.relation}${childIndex}Diseases`]
          : [];
      }
    } else {
      tempMember.age = values[`${member.relation}age`];
      if (conditionRequired) {
        tempMember.med_conditions = values[`${member.relation}Diseases`]?.length
          ? values[`${member.relation}Diseases`]
          : [];
      }
    }

    latestMembers.push(tempMember);
  });

  return latestMembers;
};

/**
 * Get the minimum premium from the suggestions
 * @param suggestion
 * @returns Premium
 */

export const getMinimumHealthPremium = (
  suggestion: Suggestion | null
): HealthPremium => {
  if (!suggestion)
    return {
      basePlan: 0,
      tax: 0,
      total: 0,
      tenure: 1
    };

  const filtered = suggestion.products.filter((product) => product.success);

  const minimum = minBy(filtered, (obj) => obj.premium.total);
  return {
    basePlan: minimum?.premium.basePlan || 0,
    tax: minimum?.premium.tax || 0,
    total: minimum?.premium.total || 0,
    tenure: 1
  };
};

export const getHealthPremium = (
  product?: string,
  suggestion?: Suggestion
): HealthPremium => {
  if (product && suggestion) {
    const suggestedProduct = suggestion.products.find(
      (suggProd) => suggProd.key === product
    );
    if (suggestion && suggestedProduct && suggestedProduct.success) {
      return {
        basePlan: suggestedProduct.premium.basePlan,
        tax: suggestedProduct.premium.tax,
        total: suggestedProduct.premium.total,
        tenure: 1
      };
    }
  }
  return {
    basePlan: 0,
    tax: 0,
    total: 0,
    tenure: 1
  };
};

export const getProductDetailsFromSlug = (
  slug: string
): FastProductDetails | undefined => {
  const product = find(
    Object.keys(HEALTH_PRODUCTS_SLUGS),
    (o: string) => HEALTH_PRODUCTS_SLUGS[o] === slug
  );
  return product
    ? {
        productName: product,
        productSlug: product ? HEALTH_PRODUCTS_SLUGS[product] : ''
      }
    : undefined;
};

export const getMaxAgeFromSelectedMembers = (members: HealthMember[]) => {
  if (!members || members.length === 0) return 99; // Default max age if no members are provided
  let max = (members && members[0]?.age) ?? 99;
  members.forEach((member) => {
    if (member?.age && member?.age > max) {
      max = member.age;
    }
  });
  return max;
};

export const getCoverUnavailableMessage = (
  amounts: number[],
  product: string
): string => {
  if (amounts.length > 2)
    return `A few cover amounts are unavailable for the given age`;

  const unAvailableMessage = amounts
    .reduce((amountInWords: Array<string | number>, amount: number) => {
      amountInWords.push(convertToINR(amount, 2, true));
      return amountInWords;
    }, [])
    .join(' & ');

  return `${unAvailableMessage} cover is not available for the given ${
    product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE ||
    product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE_VYTL
      ? 'pincode'
      : 'age'
  }`;
};

export const getHealthTaxBenefitAmount = (
  total: number,
  plan: HealthQuotePlanDetails
) => {
  const { tenure, members } = plan;
  // if the plan includes parents with age > 60 cap is 50k otherwise 25k
  const tenureFinal = tenure ?? 1;
  const cap =
    members &&
    members.length > 0 &&
    members.some((member) => member && (member?.age ?? 0) >= 60)
      ? 50000 * tenureFinal
      : 25000 * tenureFinal;
  const cappedTotal = Math.min(total, cap);
  return Math.round(cappedTotal * 0.3).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'INR'
  });
};

export const normalizeSpaces = (str?: string) => {
  return str?.replace(/\s+/g, ' ').trim();
};

export const validateMembers = (
  members: HealthMember[] | undefined,
  boundaries?: HealthProductBoundary,
  newMember?: HealthMember,
  isSubmitting = false
) => {
  if (!members || !boundaries) {
    return { isValid: false, errorMessage: 'Loading product boundaries!' };
  }

  const maxMembers = boundaries?.maxMembers ?? 4;
  const maxAdults = boundaries?.maxAdults ?? 2;
  const maxKids = boundaries?.maxKids ?? 2;
  // Create a new array with existing members plus the new member
  const updatedMembers = newMember
    ? members
      ? [...members, newMember]
      : [newMember]
    : members ?? [];

  // Check if total members exceeds maximum allowed
  if (updatedMembers.length > maxMembers) {
    return {
      isValid: false,
      errorMessage: `Maximum of ${maxMembers} members allowed`
    };
  }

  // Count adults and kids
  let adultsCount = 0;
  let kidsCount = 0;

  // Count adults and kids in the updated list
  for (const member of updatedMembers) {
    if (
      member.relation === HealthQuoteMember.Son ||
      member.relation === HealthQuoteMember.Daughter
    ) {
      kidsCount++;
    } else {
      adultsCount++;
    }
  }

  // Check if adults count exceeds maximum
  if (adultsCount > maxAdults) {
    return {
      isValid: false,
      errorMessage: `Maximum of ${maxAdults} adults allowed`
    };
  }

  // Check if kids count exceeds maximum
  if (kidsCount > maxKids) {
    return {
      isValid: false,
      errorMessage: `Maximum no of kids allowed in a policy is ${maxKids}`
    };
  }

  if (adultsCount + kidsCount > maxMembers) {
    return {
      isValid: false,
      errorMessage: `Maximum allowed members in the policy is ${maxMembers}`
    };
  }

  // Ensure gender is selected for Self and Spouse members
  const requiredGenderRelations = [HealthQuoteMember.Self, HealthQuoteMember.Spouse];
  for (const relation of requiredGenderRelations) {
    const member = updatedMembers.find((m) => m.relation === relation);
    if (member && !member.gender) {
      return {
        isValid: isSubmitting ? false : true,
        errorMessage: `Please select gender for ${relation}`
      };
    } else if (member?.gender) {
      // Self and Spouse cannot be same gender
      const otherRelation =
        relation === HealthQuoteMember.Self
          ? HealthQuoteMember.Spouse
          : HealthQuoteMember.Self;
      const otherMember = updatedMembers.find((m) => m.relation === otherRelation);
      if (otherMember?.gender === member.gender) {
        return {
          isValid: isSubmitting ? false : true,
          errorMessage: `Self and Spouse genders must be different to continue`
        };
      }
    }
  }

  // Extract relations from updated members list
  const selectedRelations = updatedMembers.map((member) => member.relation);

  // Check if the selected relations form an allowed combination
  let isValidCombination = false;

  const allowedMemberCombinations = boundaries?.allowedMemberCombinations;

  // Check against each allowed combination
  for (const allowedCombination of allowedMemberCombinations) {
    // Check if every selected relation is part of this allowed combination
    const allRelationsAllowed = selectedRelations.every((relation) =>
      allowedCombination.includes(relation)
    );

    if (allRelationsAllowed) {
      isValidCombination = true;
      break;
    }
  }

  if (!isValidCombination && newMember) {
    // Find which relations are causing the problem
    let invalidRelations: HealthQuoteMember[] = [];

    // For each allowed combination
    for (const allowedCombination of allowedMemberCombinations) {
      // Find relations in our selection that are not in this allowed combination
      const notAllowedInThisCombination = selectedRelations.filter(
        (relation) => !allowedCombination.includes(relation)
      );

      // If this combination would be valid if we removed these relations, add them to our list
      if (notAllowedInThisCombination.length > 0) {
        invalidRelations = [...invalidRelations, ...notAllowedInThisCombination];
      }
    }

    // Remove duplicates from invalidRelations
    invalidRelations = [...new Set(invalidRelations)];

    // Generate a more specific error message
    if (invalidRelations.length > 0) {
      if (invalidRelations.includes(newMember.relation)) {
        // If the new member's relation is part of the problem
        return {
          isValid: false,
          errorMessage: `This policy does not allow for the selection of ${newMember.relation} with the current member combination.`
        };
      } else {
        // The problem is with the existing combination plus the new member
        return {
          isValid: false,
          errorMessage: `Adding ${newMember.relation} to the current member selection would create an invalid combination for this policy.`
        };
      }
    } else {
      // Fallback message if we can't determine the specific issue
      return {
        isValid: false,
        errorMessage:
          'Selected members do not form a valid combination for this policy.'
      };
    }
  }

  if (!newMember && members?.length === 0) {
    return { isValid: false, errorMessage: 'Please select at least one member' };
  }

  // if a member has dependentRelation, ensure at least one of those relations is present
  for (const member of updatedMembers) {
    // Find the member's boundaries data to get dependentRelation
    const memberBoundary = boundaries.allowedMembers?.find(
      (allowedMember) => allowedMember.relation === member.relation
    );

    if (
      memberBoundary?.dependentRelation &&
      memberBoundary.dependentRelation.length > 0
    ) {
      const hasDependent = updatedMembers.some((m) =>
        memberBoundary.dependentRelation?.includes(m.relation)
      );
      if (!hasDependent) {
        return {
          isValid: isSubmitting ? false : true,
          errorMessage: `Selecting ${
            member.relation
          } requires at least one of the following members to be included: ${memberBoundary.dependentRelation.join(
            ', '
          )}.`
        };
      }
    }
  }

  // Check that there is at least one adult
  if (adultsCount === 0) {
    return {
      isValid: isSubmitting ? false : true,
      errorMessage: 'At least one adult must be included in the policy'
    };
  }

  // If all validations pass
  return { isValid: true, errorMessage: '' };
};

// Helper to check if member is a child
export const isChildMember = (relation: HealthQuoteMember) => {
  return relation === 'Son' || relation === 'Daughter';
};

/**
 * Generate the appropriate "Tell us about..." text based on selected members
 * @param members - Array of health members
 * @returns String with appropriate text
 */
export const generateTellUsAboutText = (members: HealthMember[]): string => {
  if (!members?.length) {
    return 'Tell us about you';
  }

  const relationSet = new Set(members.map((member) => member.relation));

  // Helper to check if any relation from a group exists
  const hasAny = (relations: HealthQuoteMember[]) =>
    relations.some((relation) => relationSet.has(relation));

  // Helper to check if only relations from a group exist (and no others)
  const hasOnly = (relations: HealthQuoteMember[]) => {
    const hasAllFromGroup = relations.some((relation) => relationSet.has(relation));
    const hasOnlyFromGroup = [...relationSet].every((relation) =>
      relations.includes(relation)
    );
    return hasAllFromGroup && hasOnlyFromGroup;
  };

  const children = [HealthQuoteMember.Son, HealthQuoteMember.Daughter];
  const parents = [HealthQuoteMember.Father, HealthQuoteMember.Mother];
  const inLaws = [HealthQuoteMember.FatherInLaw, HealthQuoteMember.MotherInLaw];

  // Only self
  if (hasOnly([HealthQuoteMember.Self])) {
    return 'Tell us about you';
  }

  // Only parents
  if (hasOnly(parents)) {
    return 'Tell us about your parents';
  }

  // Only in-laws
  if (hasOnly(inLaws)) {
    return 'Tell us about your in laws';
  }

  // Family combinations (self + spouse/children, or any multi-member scenario)
  if (
    (relationSet.has(HealthQuoteMember.Self) &&
      (relationSet.has(HealthQuoteMember.Spouse) || hasAny(children))) ||
    members.length > 1
  ) {
    return 'Tell us about your family';
  }

  return 'Tell us about you';
};

export const isMemberSelectionAllowed = (product: string, addOnKey: string) => {
  return MEMBER_SELECTION_SUPPORTED_ADDONS[product]?.includes(addOnKey);
};

export const getMemberGenderSymbol = (gender: HealthQuoteMemberGender) => {
  if (gender === HealthQuoteMemberGender.Male) {
    return 'M';
  }

  if (gender === HealthQuoteMemberGender.Female) {
    return 'F';
  }

  return '';
};
